import React, { useState, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdTrendingUp, MdPeople, MdEventNote, MdCalendarToday, MdArrowUpward, MdArrowDownward } from 'react-icons/md';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { dashboardAPI, metasAPI } from '../services/api';
import { SkeletonStatCard } from '../components/SkeletonLoader';

const DashboardContainer = styled.div`
  max-width: 1400px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.xl};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: ${p => p.theme.spacing.sm};
    margin-bottom: ${p => p.theme.spacing.md};
  }
`;

const StatCard = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 20px 22px;
  box-shadow: ${p => p.theme.shadows.card};
  border: 1px solid ${p => p.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  transition: box-shadow 0.2s ease, transform 0.2s ease;

  &:hover {
    box-shadow: ${p => p.theme.shadows.md};
    transform: translateY(-2px);
  }

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: 14px 14px;
    border-radius: ${p => p.theme.borderRadius.md};
    gap: 10px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const StatLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatLabel = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 6px;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  letter-spacing: -0.03em;
  line-height: 1;
  margin-bottom: 8px;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    font-size: 1.25rem;
    margin-bottom: 4px;
  }
`;

const StatChange = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.positive ? p.theme.colors.success : p.theme.colors.textLight};
  display: flex;
  align-items: center;
  gap: 3px;
  font-weight: 500;

  svg { font-size: 0.85rem; }
`;

const StatIconBox = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${p => p.gradient};
  box-shadow: 0 4px 16px ${p => p.shadowColor}40;

  svg {
    font-size: 1.5rem;
    color: white;
  }

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    svg { font-size: 1.1rem; }
  }
`;

const GridRow = styled.div`
  display: grid;
  grid-template-columns: 1.6fr 1fr;
  gap: ${p => p.theme.spacing.lg};
  margin-bottom: ${p => p.theme.spacing.lg};

  @media (max-width: ${p => p.theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    gap: ${p => p.theme.spacing.sm};
    margin-bottom: ${p => p.theme.spacing.sm};
  }
`;

const Card = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: ${p => p.theme.spacing.xl};
  box-shadow: ${p => p.theme.shadows.card};
  border: 1px solid ${p => p.theme.colors.border};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: ${p => p.theme.spacing.md};
    border-radius: ${p => p.theme.borderRadius.md};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${p => p.theme.spacing.lg};
`;

const CardTitle = styled.h2`
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.01em;
`;

const ViewAllLink = styled.button`
  color: ${p => p.theme.colors.primary};
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;

  &:hover { text-decoration: underline; }
`;

const ActionRow = styled.div`
  display: flex;
  gap: ${p => p.theme.spacing.md};
  margin-bottom: ${p => p.theme.spacing.xl};
  flex-wrap: wrap;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    margin-bottom: ${p => p.theme.spacing.md};
    gap: ${p => p.theme.spacing.sm};

    button { flex: 1; justify-content: center; }
  }
`;

const Button = styled.button`
  padding: 10px ${p => p.theme.spacing.lg};
  background: ${p => p.primary
    ? `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
    : p.theme.colors.backgroundLight};
  color: ${p => p.primary ? 'white' : p.theme.colors.textPrimary};
  border: 1px solid ${p => p.primary ? 'transparent' : p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${p => p.theme.spacing.sm};
  transition: ${p => p.theme.transitions.fast};
  box-shadow: ${p => p.primary ? `0 4px 14px ${p.theme.colors.primary}40` : p.theme.shadows.sm};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${p => p.primary ? `0 6px 20px ${p.theme.colors.primary}50` : p.theme.shadows.md};
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 520px;
`;

const Thead = styled.thead``;

const Th = styled.th`
  padding: 8px ${p => p.theme.spacing.md};
  text-align: left;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid ${p => p.theme.colors.border};
`;

const Td = styled.td`
  padding: 11px ${p => p.theme.spacing.md};
  font-size: ${p => p.theme.fontSizes.sm};
  color: ${p => p.theme.colors.textPrimary};
  border-bottom: 1px solid ${p => p.theme.colors.borderLight};

  tr:last-child & { border-bottom: none; }
`;

const ClientNameLink = styled.button`
  color: ${p => p.theme.colors.textPrimary};
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  font-family: ${p => p.theme.fonts.primary};
  transition: ${p => p.theme.transitions.fast};

  &:hover { color: ${p => p.theme.colors.primary}; }
`;

const StatusBadge = styled.span`
  padding: 3px 10px;
  border-radius: 99px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  background-color: ${p => {
    if (p.status === 'Al Corriente') return p.theme.colors.success + '18';
    if (p.status === 'Vencido') return p.theme.colors.danger + '18';
    return p.theme.colors.warning + '18';
  }};
  color: ${p => {
    if (p.status === 'Al Corriente') return p.theme.colors.success;
    if (p.status === 'Vencido') return p.theme.colors.danger;
    return p.theme.colors.warning;
  }};
`;

const ActionButton = styled.button`
  padding: 5px 14px;
  background: ${p => `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`};
  color: white;
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  transition: ${p => p.theme.transitions.fast};
  border: none;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 10px ${p => p.theme.colors.primary}40;
  }
`;

const QuickStatRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${p => p.theme.spacing.md};
`;

const QuickStatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: ${p => p.theme.colors.background};
  border-radius: ${p => p.theme.borderRadius.md};
  border: 1px solid ${p => p.theme.colors.border};
`;

const QuickStatIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${p => p.gradient};
  flex-shrink: 0;

  svg { font-size: 1.25rem; color: white; }
`;

const QuickStatContent = styled.div`
  flex: 1;
`;

const QuickStatLabel = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textLight};
  font-weight: 500;
  margin-bottom: 2px;
`;

const QuickStatValue = styled.div`
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 700;
  color: ${p => p.theme.colors.textPrimary};
  letter-spacing: -0.02em;
`;

const TooltipBox = styled.div`
  background: ${p => p.theme.colors.sidebarBg};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.md};
  padding: 10px 14px;
  box-shadow: ${p => p.theme.shadows.lg};
  font-family: ${p => p.theme.fonts.primary};
  min-width: 160px;
`;

const TooltipLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${p => p.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin-bottom: 8px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;

  &:last-child { margin-bottom: 0; }
`;

const TooltipDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => p.color};
  flex-shrink: 0;
`;

const TooltipValue = styled.span`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
`;

const TooltipName = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
  flex: 1;
`;

const MetaBarWrapper = styled.div`
  background: ${p => p.theme.colors.backgroundLight};
  border: 1px solid ${p => p.theme.colors.border};
  border-radius: ${p => p.theme.borderRadius.lg};
  padding: 14px 20px;
  margin-bottom: ${p => p.theme.spacing.xl};
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: 12px 14px;
    margin-bottom: ${p => p.theme.spacing.md};
    gap: 10px;
  }
`;

const MetaTitle = styled.span`
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${p => p.theme.colors.textSecondary};
  white-space: nowrap;
`;

const MetaAmounts = styled.span`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
`;

const MetaTrack = styled.div`
  flex: 1;
  min-width: 120px;
  height: 8px;
  background: ${p => p.theme.colors.border};
  border-radius: 99px;
  overflow: hidden;
`;

const MetaFill = styled.div`
  height: 100%;
  width: ${p => Math.min(p.pct, 100)}%;
  background: ${p =>
    p.pct >= 100
      ? `linear-gradient(90deg, ${p.theme.colors.success}, #22c55e)`
      : p.pct >= 70
      ? `linear-gradient(90deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`
      : `linear-gradient(90deg, #f59e0b, #d97706)`};
  border-radius: 99px;
  transition: width 0.6s ease;
`;

const MetaPct = styled.span`
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 800;
  color: ${p =>
    p.pct >= 100
      ? p.theme.colors.success
      : p.pct >= 70
      ? p.theme.colors.primary
      : p.theme.colors.warning};
  white-space: nowrap;
`;

const CustomTooltip = ({ active, payload, label }) => {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <TooltipBox theme={theme}>
      <TooltipLabel theme={theme}>{label}</TooltipLabel>
      {payload.map((entry, i) => (
        <TooltipRow key={i}>
          <TooltipDot color={entry.color} />
          <TooltipName theme={theme}>{entry.name}</TooltipName>
          <TooltipValue theme={theme}>
            ${(entry.value || 0).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
          </TooltipValue>
        </TooltipRow>
      ))}
    </TooltipBox>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [metaData, setMetaData] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const response = await dashboardAPI.getStats();
        setDashboardData(response.data.data);
        setError(null);
      } catch (err) {
        setError(err.message || 'Error al cargar datos del dashboard');
        console.error('Error al cargar dashboard:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  useEffect(() => {
    metasAPI.getProgreso()
      .then(r => setMetaData(r.data?.data || null))
      .catch(() => {});
  }, []);

  const stats = dashboardData?.stats || {};
  const monthlyTrends = dashboardData?.monthlyTrends || [];
  const recentClients = dashboardData?.recentClients || [];

  return (
    <DashboardContainer>
      <ActionRow>
        <Button primary onClick={() => navigate('/registrar-pago')}>
          <MdEventNote />
          Registrar Pago
        </Button>
        <Button onClick={() => navigate('/clientes/nuevo')}>
          <MdPeople />
          Nuevo Cliente
        </Button>
      </ActionRow>

      {(metaData?.tiene_meta || metaData?.tiene_meta_unidades || metaData?.tiene_meta_leads) && (() => {
        const pct  = metaData.meta_ingresos > 0
          ? Math.round((metaData.ingresos_cobrados / metaData.meta_ingresos) * 100) : 0;
        const pctU = metaData.meta_unidades > 0
          ? Math.round(((metaData.unidades_vendidas ?? 0) / metaData.meta_unidades) * 100) : 0;
        const pctL = metaData.meta_leads > 0
          ? Math.round((metaData.leads_ganados / metaData.meta_leads) * 100) : 0;
        const fmt = n => parseFloat(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        return (
          <MetaBarWrapper style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
            {metaData.tiene_meta && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <MetaTitle>Ingresos</MetaTitle>
                <MetaAmounts>${fmt(metaData.ingresos_cobrados)} / ${fmt(metaData.meta_ingresos)}</MetaAmounts>
                <MetaTrack style={{ minWidth: 100 }}>
                  <MetaFill pct={pct} />
                </MetaTrack>
                <MetaPct pct={pct}>{pct}%</MetaPct>
              </div>
            )}
            {metaData.tiene_meta_unidades && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <MetaTitle>Unidades</MetaTitle>
                <MetaAmounts>{fmt(metaData.unidades_vendidas ?? 0)} / {fmt(metaData.meta_unidades)} uds</MetaAmounts>
                <MetaTrack style={{ minWidth: 100 }}>
                  <MetaFill pct={pctU} />
                </MetaTrack>
                <MetaPct pct={pctU}>{pctU}%</MetaPct>
              </div>
            )}
            {metaData.tiene_meta_leads && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <MetaTitle>Leads</MetaTitle>
                <MetaAmounts>{metaData.leads_ganados} / {metaData.meta_leads} ganados</MetaAmounts>
                <MetaTrack style={{ minWidth: 100 }}>
                  <MetaFill pct={pctL} />
                </MetaTrack>
                <MetaPct pct={pctL}>{pctL}%</MetaPct>
              </div>
            )}
          </MetaBarWrapper>
        );
      })()}

      <StatsGrid>
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <SkeletonStatCard key={i} />)
        ) : (
          <>
            <StatCard>
              <StatLeft>
                <StatLabel>Ingresos Totales</StatLabel>
                <StatValue>${(stats.totalIncome || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</StatValue>
                <StatChange positive={stats.incomeChange > 0}>
                  {stats.incomeChange > 0 ? <MdArrowUpward /> : <MdArrowDownward />}
                  {stats.incomeChange > 0 ? '+' : ''}{stats.incomeChange || 0}% vs mes anterior
                </StatChange>
              </StatLeft>
              <StatIconBox gradient="linear-gradient(135deg, #4FD1C5, #38B2AC)" shadowColor="#4FD1C5">
                <MdTrendingUp />
              </StatIconBox>
            </StatCard>

            <StatCard>
              <StatLeft>
                <StatLabel>Clientes Activos</StatLabel>
                <StatValue>{stats.activeClients || 0}</StatValue>
                <StatChange positive>
                  <MdArrowUpward />
                  +{stats.newClients || 0} nuevos este mes
                </StatChange>
              </StatLeft>
              <StatIconBox gradient="linear-gradient(135deg, #F6AD55, #ED8936)" shadowColor="#F6AD55">
                <MdPeople />
              </StatIconBox>
            </StatCard>

            <StatCard>
              <StatLeft>
                <StatLabel>Pagos Vencidos</StatLabel>
                <StatValue>${(stats.overduePayments || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</StatValue>
                <StatChange>
                  {stats.overdueCount || 0} recibos atrasados
                </StatChange>
              </StatLeft>
              <StatIconBox gradient="linear-gradient(135deg, #FC8181, #F56565)" shadowColor="#FC8181">
                <MdEventNote />
              </StatIconBox>
            </StatCard>

            <StatCard>
              <StatLeft>
                <StatLabel>Próximos Vencimientos</StatLabel>
                <StatValue>${(stats.upcomingPayments || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</StatValue>
                <StatChange>
                  {stats.upcomingCount || 0} en los próximos 7 días
                </StatChange>
              </StatLeft>
              <StatIconBox gradient="linear-gradient(135deg, #63B3ED, #4299E1)" shadowColor="#63B3ED">
                <MdCalendarToday />
              </StatIconBox>
            </StatCard>
          </>
        )}
      </StatsGrid>

      <GridRow>
        <Card>
          <CardHeader>
            <CardTitle>Tendencias Mensuales</CardTitle>
          </CardHeader>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthlyTrends} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} vertical={false} />
              <XAxis dataKey="mes" stroke={theme.colors.textLight} tick={{ fontSize: 12, fontFamily: theme.fonts.primary }} axisLine={false} tickLine={false} />
              <YAxis stroke={theme.colors.textLight} tick={{ fontSize: 12, fontFamily: theme.fonts.primary }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: `${theme.colors.primary}08` }} />
              <Legend wrapperStyle={{ fontFamily: theme.fonts.primary, fontSize: '13px' }} />
              <Bar dataKey="total_ingresos" fill={theme.colors.primary} name="Ingresos" radius={[6, 6, 0, 0]} />
              <Bar dataKey="total_pagos" fill={theme.colors.danger} name="Vencido" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Resumen Rápido</CardTitle>
          </CardHeader>
          <QuickStatRow>
            <QuickStatItem>
              <QuickStatIcon gradient="linear-gradient(135deg, #4FD1C5, #38B2AC)">
                <MdTrendingUp />
              </QuickStatIcon>
              <QuickStatContent>
                <QuickStatLabel>Ingreso este mes</QuickStatLabel>
                <QuickStatValue>${(stats.totalIncome || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</QuickStatValue>
              </QuickStatContent>
            </QuickStatItem>
            <QuickStatItem>
              <QuickStatIcon gradient="linear-gradient(135deg, #FC8181, #F56565)">
                <MdEventNote />
              </QuickStatIcon>
              <QuickStatContent>
                <QuickStatLabel>Deuda vencida</QuickStatLabel>
                <QuickStatValue>${(stats.overduePayments || 0).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</QuickStatValue>
              </QuickStatContent>
            </QuickStatItem>
            <QuickStatItem>
              <QuickStatIcon gradient="linear-gradient(135deg, #68D391, #48BB78)">
                <MdPeople />
              </QuickStatIcon>
              <QuickStatContent>
                <QuickStatLabel>Clientes nuevos</QuickStatLabel>
                <QuickStatValue>{stats.newClients || 0} este mes</QuickStatValue>
              </QuickStatContent>
            </QuickStatItem>
            <QuickStatItem>
              <QuickStatIcon gradient="linear-gradient(135deg, #63B3ED, #4299E1)">
                <MdCalendarToday />
              </QuickStatIcon>
              <QuickStatContent>
                <QuickStatLabel>Por vencer pronto</QuickStatLabel>
                <QuickStatValue>{stats.upcomingCount || 0} pagos</QuickStatValue>
              </QuickStatContent>
            </QuickStatItem>
          </QuickStatRow>
        </Card>
      </GridRow>

      <Card>
        <CardHeader>
          <CardTitle>Clientes Recientes</CardTitle>
          <ViewAllLink onClick={() => navigate('/clientes')}>Ver todos</ViewAllLink>
        </CardHeader>

        <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Cliente</Th>
              <Th>Último Pago</Th>
              <Th>Fecha</Th>
              <Th>Estado</Th>
              <Th>Acción</Th>
            </tr>
          </Thead>
          <tbody>
            {recentClients.length > 0 ? (
              recentClients.map(client => (
                <tr key={client.cliente_id}>
                  <Td>
                    <ClientNameLink onClick={() => navigate(`/clientes/${client.cliente_id}`)}>
                      {client.nombre}
                    </ClientNameLink>
                  </Td>
                  <Td>${(client.ultimo_monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Td>
                  <Td>{client.ultima_fecha ? new Date(client.ultima_fecha).toLocaleDateString('es-MX') : 'N/A'}</Td>
                  <Td><StatusBadge status={client.estado}>{client.estado}</StatusBadge></Td>
                  <Td>
                    <ActionButton onClick={() => navigate(`/clientes/${client.cliente_id}`)}>
                      Ver
                    </ActionButton>
                  </Td>
                </tr>
              ))
            ) : (
              <tr>
                <Td colSpan="5" style={{ textAlign: 'center' }}>No hay clientes recientes</Td>
              </tr>
            )}
          </tbody>
        </Table>
        </TableWrapper>
      </Card>
    </DashboardContainer>
  );
};

export default Dashboard;
